/**
 * Sound effects, all synthesized with the Web Audio API: oscillators, filtered noise and short
 * envelopes. Nothing to download, nothing to license, and it sounds like a Flash game, which is
 * the point. Browsers only let audio start after a user gesture, so `unlock()` runs on the first
 * pointer or key press and everything before that is silently dropped.
 */

type OscType = OscillatorType;

interface Env {
  /** Seconds to full volume. */
  attack?: number;
  /** Seconds from full volume to silence (exponential-ish). */
  decay: number;
  /** Delay before the sound starts, seconds. */
  at?: number;
}

const MASTER = 0.55;

export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private muted = false;
  private loops = new Map<string, { gain: GainNode; stop: () => void }>();
  private ambient: { gain: GainNode; stop: () => void } | null = null;

  /** Create (or resume) the context. Call from a user gesture. */
  unlock(): void {
    if (typeof window === 'undefined') return;
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    if (!this.ctx) {
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : MASTER;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  get ready(): boolean {
    return !!this.ctx && this.ctx.state === 'running' && !!this.master;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(muted ? 0 : MASTER, this.ctx.currentTime, 0.02);
  }

  get isMuted(): boolean {
    return this.muted;
  }

  // ---------- building blocks ----------

  private now(): number {
    return this.ctx!.currentTime;
  }

  private env(target: AudioNode, gain: number, e: Env): GainNode {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    const t0 = this.now() + (e.at ?? 0);
    const a = e.attack ?? 0.005;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(gain, t0 + a);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + a + e.decay);
    g.connect(target);
    return g;
  }

  /** A tone sliding from f0 to f1 over the envelope. */
  private tone(type: OscType, f0: number, f1: number, gain: number, e: Env, dest?: AudioNode): void {
    if (!this.ready) return;
    const ctx = this.ctx!;
    const t0 = this.now() + (e.at ?? 0);
    const dur = (e.attack ?? 0.005) + e.decay;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(Math.max(20, f0), t0);
    o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + dur);
    o.connect(this.env(dest ?? this.master!, gain, e));
    o.start(t0);
    o.stop(t0 + dur + 0.05);
  }

  private noise(): AudioBufferSourceNode {
    const ctx = this.ctx!;
    if (!this.noiseBuf) {
      this.noiseBuf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
      const d = this.noiseBuf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    }
    const s = ctx.createBufferSource();
    s.buffer = this.noiseBuf;
    s.loop = true;
    return s;
  }

  /**
   * Wobble `param` with noise slowed down to `hz`: the sputter of a fuse, the flicker of a fire.
   * Slowed noise is mostly small, so `depth` is a few units to get a visible wobble. Returns the
   * source so the caller can stop it with the rest of its loop.
   */
  private flicker(param: AudioParam, hz: number, depth: number): AudioBufferSourceNode {
    const ctx = this.ctx!;
    const s = this.noise();
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = hz;
    const g = ctx.createGain();
    g.gain.value = depth;
    s.connect(lp);
    lp.connect(g);
    g.connect(param);
    s.start();
    return s;
  }

  /** A resonant band on the way to the master: gives a buzz a throat (a grunt) or a hull (a groan). */
  private formant(freq: number, q: number): AudioNode {
    const f = this.ctx!.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = freq;
    f.Q.value = q;
    f.connect(this.master!);
    return f;
  }

  /** Filtered noise: the filter sweeps from f0 to f1 over the envelope. */
  private hiss(type: BiquadFilterType, f0: number, f1: number, q: number, gain: number, e: Env, dest?: AudioNode): void {
    if (!this.ready) return;
    const ctx = this.ctx!;
    const t0 = this.now() + (e.at ?? 0);
    const dur = (e.attack ?? 0.005) + e.decay;
    const s = this.noise();
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.Q.value = q;
    f.frequency.setValueAtTime(Math.max(20, f0), t0);
    f.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + dur);
    s.connect(f);
    f.connect(this.env(dest ?? this.master!, gain, e));
    s.start(t0);
    s.stop(t0 + dur + 0.05);
  }

  // ---------- one-shots ----------

  /** A UI tick. */
  click(): void {
    this.tone('sine', 2600, 1800, 0.12, { decay: 0.035 });
    this.hiss('highpass', 3000, 3000, 1, 0.05, { decay: 0.02 });
  }

  /** Coins changing hands. */
  coin(soft = false): void {
    const g = soft ? 0.12 : 0.22;
    this.tone('sine', 1760, 1760, g, { decay: 0.1 });
    this.tone('sine', 2349, 2349, g * 0.8, { decay: 0.16, at: 0.06 });
  }

  /** Your turn: a three-note chime. */
  turn(): void {
    for (const [i, f] of [523, 659, 784].entries()) this.tone('sine', f, f, 0.16, { decay: 0.28, at: i * 0.09 });
  }

  /** Something leaves a hand; power 0..1 sets how big the swish is. */
  whoosh(power: number, soft = false): void {
    const g = (soft ? 0.12 : 0.22) * (0.5 + 0.5 * power);
    this.hiss('bandpass', 300, 1800 + power * 1500, 1.2, g, { attack: 0.03, decay: 0.18 + power * 0.1 });
  }

  /** A mortar leaving the tube: a hollow thoonk. */
  mortar(): void {
    this.tone('sine', 170, 55, 0.4, { attack: 0.004, decay: 0.22 });
    this.hiss('lowpass', 900, 200, 1, 0.25, { decay: 0.16 });
  }

  /** An RPG's roar, kept until `stopLoop(id)`. */
  startRocket(id: string, homing: boolean): void {
    if (!this.ready || this.loops.has(id)) return;
    const ctx = this.ctx!;
    const s = this.noise();
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = homing ? 900 : 650;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, this.now());
    g.gain.linearRampToValueAtTime(0.16, this.now() + 0.08);
    s.connect(f);
    f.connect(g);
    g.connect(this.master!);
    s.start();
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.value = homing ? 180 : 110;
    const og = ctx.createGain();
    og.gain.value = 0.05;
    o.connect(og);
    og.connect(g);
    o.start();
    this.loops.set(id, { gain: g, stop: () => { s.stop(); o.stop(); } });
  }

  /** A lit fuse's sputtering hiss (a TNT charge waiting to go), kept until `stopLoop(id)`. */
  startFuse(id: string): void {
    if (!this.ready || this.loops.has(id)) return;
    const ctx = this.ctx!;
    const t = this.now();
    const s = this.noise();
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = 3400;
    f.Q.value = 0.8;
    const level = ctx.createGain();
    level.gain.value = 0.06;
    const flick = this.flicker(level.gain, 16, 3);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(1, t + 0.05);
    s.connect(f);
    f.connect(level);
    level.connect(g);
    g.connect(this.master!);
    s.start();
    this.loops.set(id, { gain: g, stop: () => { s.stop(); flick.stop(); } });
  }

  /**
   * A burning vessel: a soft crackle, kept until `stopLoop(id)`. It is a bed under the fight,
   * not an event, so it sits well below every one-shot.
   */
  startFire(id: string): void {
    if (!this.ready || this.loops.has(id)) return;
    const ctx = this.ctx!;
    const t = this.now();
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(1, t + 0.6);
    g.connect(this.master!);
    // The body of the fire: a low roar that breathes.
    const roar = this.noise();
    const rf = ctx.createBiquadFilter();
    rf.type = 'lowpass';
    rf.frequency.value = 320;
    const rg = ctx.createGain();
    rg.gain.value = 0.045;
    roar.connect(rf);
    rf.connect(rg);
    rg.connect(g);
    const roarFlick = this.flicker(rg.gain, 5, 2);
    // The crackle: a brighter band that pops at random, silent in between.
    const pop = this.noise();
    const pf = ctx.createBiquadFilter();
    pf.type = 'bandpass';
    pf.frequency.value = 2400;
    pf.Q.value = 0.7;
    const pg = ctx.createGain();
    pg.gain.value = 0;
    pop.connect(pf);
    pf.connect(pg);
    pg.connect(g);
    const popFlick = this.flicker(pg.gain, 45, 1.4);
    roar.start();
    pop.start();
    this.loops.set(id, { gain: g, stop: () => { roar.stop(); pop.stop(); roarFlick.stop(); popFlick.stop(); } });
  }

  /**
   * The tunneler boring: a grinding motor, kept until `stopLoop(id)`. Two low saws a hair apart
   * beat against each other like a straining engine, and a band of noise above them is the
   * cutter in the dirt; a slow wobble on both keeps it from sounding like a test tone.
   */
  startDrill(id: string): void {
    if (!this.ready || this.loops.has(id)) return;
    const ctx = this.ctx!;
    const t = this.now();
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(1, t + 0.15);
    g.connect(this.master!);
    // The motor: detuned saws through a lowpass that the wobble opens and closes.
    const mf = ctx.createBiquadFilter();
    mf.type = 'lowpass';
    mf.frequency.value = 420;
    mf.Q.value = 2;
    const mg = ctx.createGain();
    mg.gain.value = 0.09;
    mf.connect(mg);
    mg.connect(g);
    const oscs: OscillatorNode[] = [];
    for (const hz of [58, 61]) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = hz;
      o.connect(mf);
      o.start();
      oscs.push(o);
    }
    // The cutter: noise through a band that chews, with a faster sputter on its level.
    const s = this.noise();
    const nf = ctx.createBiquadFilter();
    nf.type = 'bandpass';
    nf.frequency.value = 900;
    nf.Q.value = 1.2;
    const ng = ctx.createGain();
    ng.gain.value = 0.07;
    s.connect(nf);
    nf.connect(ng);
    ng.connect(g);
    s.start();
    const grit = this.flicker(ng.gain, 30, 1.2);
    // The wobble: a slow sine on the motor's filter and the cutter's band, so the machine labours.
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 1.3;
    const lfoMotor = ctx.createGain();
    lfoMotor.gain.value = 160;
    const lfoCutter = ctx.createGain();
    lfoCutter.gain.value = 250;
    lfo.connect(lfoMotor);
    lfo.connect(lfoCutter);
    lfoMotor.connect(mf.frequency);
    lfoCutter.connect(nf.frequency);
    lfo.start();
    this.loops.set(id, { gain: g, stop: () => { for (const o of oscs) o.stop(); s.stop(); grit.stop(); lfo.stop(); } });
  }

  stopLoop(id: string): void {
    const l = this.loops.get(id);
    if (!l || !this.ctx) return;
    this.loops.delete(id);
    l.gain.gain.setTargetAtTime(0.0001, this.now(), 0.04);
    setTimeout(() => l.stop(), 250);
  }

  /** A ball's boing, a grenade's tink, a plank's clack, or a rubber bomb's springy boing. */
  bounce(kind: 'ball' | 'tink' | 'clack' | 'boing', soft = false): void {
    const g = soft ? 0.5 : 1;
    if (kind === 'ball') this.tone('sine', 420, 260, 0.14 * g, { decay: 0.12 });
    else if (kind === 'tink') {
      this.tone('triangle', 2400, 2000, 0.07 * g, { decay: 0.06 });
      this.hiss('highpass', 4000, 4000, 1, 0.03 * g, { decay: 0.02 });
    } else if (kind === 'boing') {
      this.boing(0.16 * g);
    } else {
      this.hiss('bandpass', 900, 500, 2, 0.14 * g, { decay: 0.06 });
      this.tone('sine', 180, 120, 0.1 * g, { decay: 0.07 });
    }
  }

  /** A spring: a sine bent up fast and let down slowly, with a wobble that dies away. */
  private boing(gain: number): void {
    if (!this.ready) return;
    const ctx = this.ctx!;
    const t0 = this.now();
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(170, t0);
    o.frequency.exponentialRampToValueAtTime(620, t0 + 0.05);
    o.frequency.exponentialRampToValueAtTime(240, t0 + 0.3);
    const wob = ctx.createOscillator();
    wob.type = 'sine';
    wob.frequency.value = 22;
    const depth = ctx.createGain();
    depth.gain.setValueAtTime(60, t0);
    depth.gain.exponentialRampToValueAtTime(1, t0 + 0.3);
    wob.connect(depth);
    depth.connect(o.frequency);
    o.connect(this.env(this.master!, gain, { attack: 0.006, decay: 0.28 }));
    o.start(t0);
    wob.start(t0);
    o.stop(t0 + 0.35);
    wob.stop(t0 + 0.35);
    this.hiss('highpass', 3500, 3500, 1, 0.025, { decay: 0.015 });
  }

  /** An explosion sized by its blast radius (6 is a bullet in the dirt; 130 is a big grenade). */
  boom(radius: number): void {
    if (radius <= 12) {
      this.hiss('lowpass', 1800, 300, 1, 0.12, { decay: 0.08 });
      return;
    }
    const k = Math.min(1, radius / 110);
    this.tone('sine', 90 + k * 30, 28, 0.45 + k * 0.35, { attack: 0.004, decay: 0.35 + k * 0.35 });
    this.hiss('lowpass', 2500, 120, 0.8, 0.4 + k * 0.3, { attack: 0.004, decay: 0.3 + k * 0.4 });
    this.hiss('bandpass', 1200, 400, 1.5, 0.18 * k, { decay: 0.12 });
  }

  /** Something going in the water; `at` delays it (a vessel's splash comes after its fireball). */
  splash(at = 0): void {
    this.tone('sine', 520, 110, 0.18, { decay: 0.16, at });
    this.hiss('bandpass', 2200, 500, 0.8, 0.3, { attack: 0.02, decay: 0.42, at });
    this.hiss('highpass', 1500, 600, 0.5, 0.1, { decay: 0.6, at: at + 0.1 });
  }

  /** A vessel going up: the blast as wide as the boat, the hull groaning as it tears, and a long rumble under it all. */
  bigBoom(w: number): void {
    if (!this.ready) return;
    this.boom(w);
    this.tone('sawtooth', 120, 48, 0.1, { attack: 0.15, decay: 1.0, at: 0.12 }, this.formant(420, 5));
    this.tone('sawtooth', 95, 40, 0.06, { attack: 0.2, decay: 1.1, at: 0.2 }, this.formant(260, 6));
    this.hiss('lowpass', 180, 50, 0.7, 0.3, { attack: 0.15, decay: 1.9, at: 0.08 });
  }

  /** A boat going under: the water closing over it, then bubbles for a second or so. `at` delays it. */
  sink(at = 0): void {
    this.splash(at);
    this.tone('sine', 260, 70, 0.2, { attack: 0.02, decay: 0.35, at });
    this.hiss('lowpass', 600, 220, 0.8, 0.2, { attack: 0.05, decay: 0.8, at });
    // The gurgle: a handful of bubbles, each a quick blip upward, spaced unevenly.
    const gaps = [0.32, 0.11, 0.09, 0.15, 0.08, 0.14, 0.1, 0.17, 0.12];
    let t = at;
    gaps.forEach((gap, i) => {
      t += gap;
      const f = 140 + ((i * 53) % 110);
      this.tone('sine', f, f * 2.4, 0.07, { attack: 0.008, decay: 0.07, at: t });
    });
    this.hiss('bandpass', 350, 900, 2, 0.07, { attack: 0.3, decay: 0.9, at: at + 0.3 });
  }

  /** Guns. */
  shot(weapon: string): void {
    switch (weapon) {
      case 'sniper':
        this.hiss('bandpass', 3000, 400, 0.6, 0.5, { decay: 0.13 });
        this.tone('sine', 220, 60, 0.3, { decay: 0.18 });
        this.hiss('lowpass', 800, 200, 1, 0.16, { decay: 0.5, at: 0.05 });
        break;
      case 'smg':
        // Five quick cracks 55 ms apart, each lighter than the pistol's: a rattle, not a drum roll.
        for (let i = 0; i < 5; i++) {
          const at = i * 0.055;
          this.hiss('bandpass', 2800, 900, 0.9, 0.22, { decay: 0.045, at });
          this.tone('sine', 260, 100, 0.1, { decay: 0.05, at });
        }
        break;
      case 'railgun':
        // A quarter second of charge whine climbing, then a deep electric crack with a long tail.
        this.tone('sawtooth', 180, 2200, 0.07, { attack: 0.22, decay: 0.05 });
        this.tone('sine', 360, 3600, 0.05, { attack: 0.22, decay: 0.04 });
        this.hiss('highpass', 7000, 1200, 0.7, 0.5, { decay: 0.12, at: 0.25 });
        this.tone('square', 70, 30, 0.2, { attack: 0.004, decay: 0.4, at: 0.25 });
        this.tone('sine', 140, 26, 0.5, { attack: 0.004, decay: 0.55, at: 0.25 });
        this.hiss('lowpass', 1400, 120, 0.8, 0.28, { attack: 0.03, decay: 1.0, at: 0.3 });
        break;
      case 'shotgun':
        this.hiss('lowpass', 3500, 250, 0.7, 0.6, { decay: 0.22 });
        this.tone('sine', 160, 45, 0.35, { decay: 0.25 });
        break;
      case 'harpoon':
        this.tone('triangle', 320, 70, 0.28, { decay: 0.3 });
        this.hiss('bandpass', 2500, 1400, 3, 0.14, { attack: 0.02, decay: 0.35 });
        break;
      default:
        this.hiss('bandpass', 2600, 700, 0.8, 0.35, { decay: 0.07 });
        this.tone('sine', 300, 90, 0.18, { decay: 0.08 });
    }
  }

  /** Close quarters. */
  swing(weapon: string): void {
    switch (weapon) {
      case 'bat':
        this.hiss('bandpass', 1500, 600, 1.5, 0.35, { decay: 0.05 });
        this.tone('triangle', 880, 700, 0.14, { decay: 0.22, at: 0.02 });
        break;
      case 'mallet':
        this.tone('sine', 120, 40, 0.5, { decay: 0.3 });
        this.hiss('lowpass', 900, 150, 1, 0.3, { decay: 0.14 });
        break;
      case 'pike':
        this.hiss('highpass', 2500, 1200, 1, 0.2, { decay: 0.05 });
        this.tone('sine', 700, 180, 0.14, { decay: 0.1, at: 0.02 });
        break;
      default:
        this.tone('sine', 200, 60, 0.3, { decay: 0.12 });
        this.hiss('bandpass', 1000, 400, 1, 0.25, { decay: 0.08 });
    }
  }

  /** A mine's beep. */
  beep(): void {
    this.tone('square', 1300, 1300, 0.08, { decay: 0.06 });
  }

  /** A hook biting. */
  clink(): void {
    this.tone('triangle', 2400, 2300, 0.12, { decay: 0.22 });
    this.tone('triangle', 3150, 3000, 0.07, { decay: 0.18 });
    this.hiss('highpass', 5000, 5000, 1, 0.04, { decay: 0.03 });
  }

  /** Something heavy set down: a plank, sandbags, a mended panel. */
  thud(): void {
    this.tone('sine', 150, 55, 0.3, { decay: 0.16 });
    this.hiss('lowpass', 1200, 200, 1, 0.18, { decay: 0.1 });
  }

  /** A pickaxe biting dirt, `n` times. */
  dig(n: number): void {
    for (let i = 0; i < n; i++) {
      const at = i * 0.045;
      this.hiss('bandpass', 1600, 500, 1.2, 0.24, { decay: 0.06, at });
      this.tone('sine', 220, 90, 0.16, { decay: 0.06, at });
    }
  }

  /** The tunneler's bites: `n` soft crunches of dirt giving way, well under the drill loop that covers them. */
  crunch(n: number): void {
    for (let i = 0; i < n; i++) {
      const at = i * 0.05;
      this.hiss('lowpass', 1100, 250, 1, 0.1, { attack: 0.01, decay: 0.09, at });
      this.tone('sine', 110, 60, 0.07, { attack: 0.01, decay: 0.08, at });
    }
  }

  /** A medkit (or a second wind). */
  heal(): void {
    this.tone('sine', 520, 520, 0.14, { decay: 0.14 });
    this.tone('sine', 780, 780, 0.14, { decay: 0.22, at: 0.1 });
    this.tone('sine', 1040, 1040, 0.1, { decay: 0.3, at: 0.2 });
  }

  /** A raft panel splintering. */
  crack(): void {
    this.hiss('highpass', 1800, 900, 1, 0.3, { decay: 0.08 });
    this.hiss('bandpass', 500, 250, 1.5, 0.2, { decay: 0.14, at: 0.03 });
    this.tone('sine', 140, 60, 0.2, { decay: 0.15, at: 0.02 });
  }

  /** A mole taking damage; louder for bigger numbers. */
  hit(amount: number): void {
    const g = Math.min(1, amount / 45);
    this.tone('triangle', 260 - g * 60, 120, 0.08 + 0.1 * g, { decay: 0.1 });
  }

  /** Headshot / direct hit / long shot. */
  sting(kind: 'headshot' | 'direct' | 'long'): void {
    const notes = kind === 'headshot' ? [1568, 2093] : kind === 'direct' ? [1175, 1568] : [988, 1319];
    notes.forEach((f, i) => this.tone('sine', f, f, 0.12, { decay: 0.2, at: i * 0.08 }));
  }

  /** Round and match results. */
  fanfare(kind: 'win' | 'lose' | 'draw' | 'round'): void {
    const seq = kind === 'win' ? [523, 659, 784, 1047] : kind === 'lose' ? [392, 349, 311, 262] : kind === 'draw' ? [440, 440] : [659, 784];
    seq.forEach((f, i) => {
      this.tone('triangle', f, f, 0.16, { decay: i === seq.length - 1 ? 0.6 : 0.22, at: i * 0.14 });
      this.tone('sine', f / 2, f / 2, 0.08, { decay: 0.3, at: i * 0.14 });
    });
  }

  /** A new wave of raiders. */
  horn(): void {
    this.tone('sawtooth', 220, 220, 0.1, { attack: 0.05, decay: 0.45 });
    this.tone('sawtooth', 330, 330, 0.08, { attack: 0.05, decay: 0.45, at: 0.02 });
  }

  // ---------- the new arsenal ----------

  /** A crossbow string snapping forward, then the bolt's short whistle. */
  twang(): void {
    this.tone('triangle', 210, 150, 0.2, { attack: 0.003, decay: 0.12 });
    this.tone('sine', 420, 300, 0.1, { attack: 0.003, decay: 0.08 });
    this.hiss('highpass', 3500, 1500, 1, 0.12, { decay: 0.03 });
    this.tone('sine', 2400, 1700, 0.045, { attack: 0.03, decay: 0.14, at: 0.04 });
    this.hiss('bandpass', 2600, 1900, 6, 0.07, { attack: 0.03, decay: 0.16, at: 0.04 });
  }

  /**
   * An airstrike: a plane drones across the top of the map the way its bombs drift (`dir` 1 is
   * left to right), panned across when the browser can pan, and the bombs whistle down behind it.
   */
  plane(dir: 1 | -1): void {
    if (!this.ready) return;
    const ctx = this.ctx!;
    const t0 = this.now();
    const dur = 1.5;
    let dest: AudioNode = this.master!;
    if (typeof ctx.createStereoPanner === 'function') {
      const pan = ctx.createStereoPanner();
      pan.pan.setValueAtTime(-0.8 * dir, t0);
      pan.pan.linearRampToValueAtTime(0.8 * dir, t0 + dur);
      pan.connect(this.master!);
      dest = pan;
    }
    // Two saws a hair apart beat like a propeller; it opens up as it nears and the pitch drops as it passes.
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(500, t0);
    f.frequency.exponentialRampToValueAtTime(1300, t0 + dur * 0.5);
    f.frequency.exponentialRampToValueAtTime(350, t0 + dur);
    f.connect(this.env(dest, 0.11, { attack: 0.5, decay: 1.0 }));
    for (const hz of [112, 115]) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.setValueAtTime(hz, t0);
      o.frequency.setValueAtTime(hz, t0 + dur * 0.4);
      o.frequency.exponentialRampToValueAtTime(hz * 0.72, t0 + dur);
      o.connect(f);
      o.start(t0);
      o.stop(t0 + dur + 0.1);
    }
    // The bombs: a whistle falling for a second and a bit.
    this.tone('sine', 2100, 500, 0.05, { attack: 0.25, decay: 1.0, at: 0.35 });
    this.hiss('bandpass', 2400, 600, 7, 0.06, { attack: 0.25, decay: 1.0, at: 0.35 });
  }

  /** Timber under strain: a raft tilting past its slide angle, a catapult arm winding back. */
  creak(): void {
    if (!this.ready) return;
    const ctx = this.ctx!;
    const t0 = this.now();
    // A low saw with a fast wobble in its pitch through a narrow band: wood grinding on wood.
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(140, t0);
    o.frequency.linearRampToValueAtTime(190, t0 + 0.12);
    o.frequency.linearRampToValueAtTime(120, t0 + 0.28);
    const wob = ctx.createOscillator();
    wob.type = 'sine';
    wob.frequency.value = 28;
    const depth = ctx.createGain();
    depth.gain.value = 18;
    wob.connect(depth);
    depth.connect(o.frequency);
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = 700;
    f.Q.value = 2.5;
    o.connect(f);
    f.connect(this.env(this.master!, 0.14, { attack: 0.04, decay: 0.26 }));
    o.start(t0);
    wob.start(t0);
    o.stop(t0 + 0.4);
    wob.stop(t0 + 0.4);
    this.hiss('bandpass', 900, 400, 3, 0.06, { attack: 0.04, decay: 0.24 });
  }

  /** A catapult letting fly: the arm's creak, the heavy thunk of it hitting the crossbar, then the boulder's low whoosh. */
  catapult(): void {
    this.creak();
    this.tone('sine', 130, 40, 0.45, { attack: 0.004, decay: 0.3, at: 0.2 });
    this.hiss('lowpass', 900, 120, 1, 0.3, { decay: 0.18, at: 0.2 });
    this.hiss('bandpass', 400, 140, 1.2, 0.18, { decay: 0.12, at: 0.21 });
    this.hiss('lowpass', 250, 1100, 0.8, 0.22, { attack: 0.08, decay: 0.5, at: 0.24 });
  }

  /** The massive bomb leaving two hands: a grunt and a big, slow whoosh. */
  heave(): void {
    if (!this.ready) return;
    this.tone('sawtooth', 150, 90, 0.07, { attack: 0.04, decay: 0.22 }, this.formant(480, 2));
    this.tone('sine', 170, 100, 0.1, { attack: 0.04, decay: 0.2 });
    this.hiss('bandpass', 160, 800, 0.9, 0.32, { attack: 0.1, decay: 0.55 });
    this.hiss('lowpass', 400, 160, 0.8, 0.14, { attack: 0.12, decay: 0.5 });
  }

  /** A sticky bomb leaving the hand: the usual swish with some slop in it. */
  wetWhoosh(power: number): void {
    this.whoosh(power);
    this.tone('sine', 360, 130, 0.07, { attack: 0.02, decay: 0.16 });
    this.hiss('bandpass', 700, 260, 2.5, 0.12, { attack: 0.02, decay: 0.22 });
  }

  /** A mole blinking out and back in: two sines a hair apart sweeping up through a burst of static, then a shimmer. */
  zap(): void {
    this.hiss('highpass', 1800, 6000, 0.8, 0.22, { decay: 0.1 });
    this.tone('sine', 280, 2300, 0.12, { attack: 0.01, decay: 0.32 });
    this.tone('sine', 287, 2370, 0.1, { attack: 0.01, decay: 0.36 });
    this.hiss('bandpass', 4200, 6500, 9, 0.07, { attack: 0.1, decay: 0.5, at: 0.12 });
  }

  /** A sticky bomb hitting something: a wet slap. */
  splat(): void {
    this.hiss('lowpass', 1600, 300, 1.2, 0.3, { attack: 0.003, decay: 0.09 });
    this.tone('sine', 420, 70, 0.22, { attack: 0.003, decay: 0.12 });
    this.hiss('bandpass', 700, 300, 2, 0.12, { decay: 0.14, at: 0.03 });
  }

  /** A bolt driving into wood or dirt, and its shaft quivering. */
  thunk(): void {
    this.tone('sine', 260, 70, 0.3, { attack: 0.003, decay: 0.09 });
    this.hiss('lowpass', 2200, 300, 1, 0.22, { decay: 0.06 });
    this.tone('triangle', 900, 700, 0.05, { decay: 0.12, at: 0.01 });
  }

  /** The camera pulling out between turns: a quiet breath of air, nothing more. */
  pullOut(): void {
    this.hiss('lowpass', 220, 700, 0.8, 0.05, { attack: 0.25, decay: 0.5 });
  }

  // ---------- ambience ----------

  /** A quiet bed: the sea on water maps, wind elsewhere, drips and a draught underground. `null` stops it. */
  ambience(kind: 'sea' | 'wind' | 'cave' | null): void {
    if (this.ambient) {
      const a = this.ambient;
      this.ambient = null;
      if (this.ctx) a.gain.gain.setTargetAtTime(0.0001, this.now(), 0.5);
      setTimeout(() => a.stop(), 2000);
    }
    if (!kind || !this.ready) return;
    if (kind === 'cave') {
      this.cave();
      return;
    }
    const ctx = this.ctx!;
    const s = this.noise();
    const f = ctx.createBiquadFilter();
    f.type = kind === 'sea' ? 'lowpass' : 'bandpass';
    f.frequency.value = kind === 'sea' ? 380 : 700;
    f.Q.value = kind === 'sea' ? 0.7 : 0.6;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, this.now());
    g.gain.linearRampToValueAtTime(kind === 'sea' ? 0.14 : 0.08, this.now() + 2);
    // Swell: a slow wobble on the filter and the level.
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = kind === 'sea' ? 0.11 : 0.07;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = kind === 'sea' ? 160 : 260;
    lfo.connect(lfoGain);
    lfoGain.connect(f.frequency);
    s.connect(f);
    f.connect(g);
    g.connect(this.master!);
    s.start();
    lfo.start();
    this.ambient = { gain: g, stop: () => { s.stop(); lfo.stop(); } };
  }

  /**
   * Underground: a low draught that barely moves, and water dripping somewhere out of sight.
   * Each drip is a short blip that goes into a feedback delay, so it comes back off the walls a
   * few times, quieter each time; the drips are spaced unevenly by a timer that the stop clears.
   */
  private cave(): void {
    const ctx = this.ctx!;
    const t = this.now();
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(1, t + 2);
    g.connect(this.master!);
    // The draught: noise kept very low, with a slow breath on its band.
    const s = this.noise();
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 180;
    f.Q.value = 0.8;
    const wg = ctx.createGain();
    wg.gain.value = 0.07;
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.05;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 70;
    lfo.connect(lfoGain);
    lfoGain.connect(f.frequency);
    s.connect(f);
    f.connect(wg);
    wg.connect(g);
    s.start();
    lfo.start();
    // The echo: a delay feeding back into itself through a lowpass, so each return is duller than the last.
    const echoIn = ctx.createGain();
    echoIn.gain.value = 1;
    const delay = ctx.createDelay(1);
    delay.delayTime.value = 0.23;
    const fb = ctx.createGain();
    fb.gain.value = 0.45;
    const dull = ctx.createBiquadFilter();
    dull.type = 'lowpass';
    dull.frequency.value = 1800;
    echoIn.connect(g);
    echoIn.connect(delay);
    delay.connect(dull);
    dull.connect(fb);
    fb.connect(delay);
    fb.connect(g);
    let timer = 0;
    const drip = () => {
      if (this.ambient?.gain !== g) return;
      const f0 = 900 + Math.random() * 1400;
      this.tone('sine', f0, f0 * 1.6, 0.05 + Math.random() * 0.04, { attack: 0.004, decay: 0.07 }, echoIn);
      timer = window.setTimeout(drip, 700 + Math.random() * 2600);
    };
    timer = window.setTimeout(drip, 800);
    this.ambient = { gain: g, stop: () => { clearTimeout(timer); s.stop(); lfo.stop(); } };
  }

  /** Stop everything that loops (leaving a match). */
  stopAll(): void {
    for (const id of [...this.loops.keys()]) this.stopLoop(id);
    this.ambience(null);
  }
}

export const sfx = new Sfx();

/** Unlock on the first gesture and tick on every button. Call once at startup. */
export function installAudioListeners(): void {
  if (typeof document === 'undefined') return;
  const unlock = () => sfx.unlock();
  document.addEventListener('pointerdown', unlock, { capture: true });
  document.addEventListener('keydown', unlock, { capture: true });
  document.addEventListener(
    'click',
    (e) => {
      const el = e.target as HTMLElement | null;
      const btn = el?.closest?.('button, [role="tab"], [role="menuitem"]');
      if (btn && !(btn as HTMLButtonElement).disabled) sfx.click();
    },
    { capture: true },
  );
}
